import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Flame, Hammer, Lock, Mountain, Play, Rocket, Star, Target } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../lib/api';

const levelIcons = {
  flame: Flame,
  target: Target,
  mountain: Mountain,
  hammer: Hammer,
  rocket: Rocket
};

const levelNames = {
  en: { 1: 'Determination', 2: 'Discipline', 3: 'Perseverance', 4: 'Hard-working', 5: 'Breakthrough' },
  zh: { 1: '\u51b3\u5fc3', 2: '\u81ea\u5f8b', 3: '\u6bc5\u529b', 4: '\u52e4\u52b3', 5: '\u7a81\u7834' }
};

function LevelPage() {
  const { levelNum } = useParams();
  const [level, setLevel] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const search = location.search || '?monster=level';

  useEffect(() => {
    const fetchStages = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/levels/${levelNum}/stages`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLevel(response.data.level);
        setStages(response.data.stages);
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error(language === 'zh' ? '\u5173\u5361\u5c1a\u672a\u89e3\u9501' : 'Level not unlocked yet');
        } else {
          toast.error(language === 'zh' ? '\u65e0\u6cd5\u52a0\u8f7d\u9636\u6bb5' : 'Failed to load stages');
        }
        navigate(`/levels${search}`);
      } finally {
        setLoading(false);
      }
    };

    fetchStages();
  }, [language, levelNum, navigate, search]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-50 to-pink-50">
        <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-violet-500"></div>
      </div>
    );
  }

  const Icon = levelIcons[level?.icon] || Flame;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="level-page">
      <header className="px-4 py-8" style={{ backgroundColor: `${level?.color}15` }}>
        <div className="mx-auto max-w-4xl">
          <button
            onClick={() => navigate(`/levels${search}`)}
            className="mb-4 flex items-center gap-2 font-medium text-zinc-600 hover:text-zinc-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            {t('back')}
          </button>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            <div className="rounded-xl p-4" style={{ backgroundColor: `${level?.color}20` }}>
              <Icon className="h-10 w-10" style={{ color: level?.color }} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-500">{t('level')} {levelNum}</p>
              <h1 className="text-3xl font-black" style={{ color: level?.color }} data-testid="level-title">
                {levelNames[language][parseInt(levelNum, 10)]}
              </h1>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        <h2 className="mb-6 text-2xl font-black text-zinc-900">{t('stages')}</h2>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stages.map((stage, index) => {
            const isLocked = !stage.is_unlocked;
            const isCompleted = stage.is_completed;

            return (
              <motion.div
                key={stage.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => !isLocked && navigate(`/play/${stage.id}${search}`)}
                className={`rounded-2xl border-2 bg-white p-6 text-center transition-all ${
                  isLocked
                    ? 'cursor-not-allowed border-zinc-200 opacity-60'
                    : isCompleted
                      ? 'cursor-pointer border-green-300 bg-green-50 hover:shadow-md'
                      : 'cursor-pointer border-zinc-200 hover:border-violet-300 hover:shadow-md'
                }`}
                data-testid={`stage-card-${stage.stage_num}`}
              >
                <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
                  isLocked
                    ? 'bg-zinc-100'
                    : isCompleted
                      ? 'bg-green-500'
                      : 'bg-gradient-to-br from-violet-500 to-pink-500'
                }`}>
                  {isLocked ? (
                    <Lock className="h-8 w-8 text-zinc-400" />
                  ) : isCompleted ? (
                    <CheckCircle className="h-8 w-8 text-white" />
                  ) : (
                    <Play className="ml-1 h-8 w-8 text-white" />
                  )}
                </div>

                <h3 className="mb-2 text-xl font-black text-zinc-900">
                  {language === 'zh' ? stage.name_zh : stage.name_en}
                </h3>

                <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span>{stage.points_reward} {t('points')}</span>
                </div>

                {isCompleted && (
                  <span className="mt-3 inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
                    {t('completed')}
                  </span>
                )}

                {!isLocked && !isCompleted && (
                  <button className="mt-4 w-full rounded-xl bg-violet-500 px-4 py-2 font-bold text-white transition-colors hover:bg-violet-600">
                    {t('start_stage')}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default LevelPage;
